import {
  buildCarePlanGeniePreview,
  CarePlanGenieFieldDefinition,
  CarePlanGenieQuestion,
} from "./carePlanGenieShared";
import { EMAR_DAILY_TRACKING_CATEGORY_OPTIONS } from "../utils/emarDailyTrackingUtils";

export interface HealthIndicatorsGenieSectionValues {
  anyPain?: boolean;
  anyPainStrengthDetails?: string;
  anyPainAssistanceDetails?: string;
  anyWeightChanges?: boolean;
  currentWeight?: string;
  currentHeight?: string;
  anyWeightStrengthDetails?: string;
  anyWeightAssistanceDetails?: string;
  anyVitalSignIssues?: boolean;
  anyVitalSignStrengthDetails?: string;
  anyVitalSignAssistanceDetails?: string;
  anyHospitalizations?: boolean;
  anyHospitalizationsStrengthDetails?: string;
  anyHospitalizationsAssistanceDetails?: string;
  anyOtherHealthIndicatorsDetails?: boolean | string;
  anyOtherHealthIndicatorsStrengthDetails?: string;
  anyOtherHealthIndicatorsAssistanceDetails?: string;
  anyAllergies?: boolean;
  allergySubstanceDetails?: string;
  allergyReactionDetails?: string;
}

export const HEALTH_INDICATORS_GENIE_FIELD_DEFINITIONS: Record<string, CarePlanGenieFieldDefinition> = {
  anyPain: { key: "anyPain", label: "Any pain", type: "boolean", applyMode: "replace" },
  anyPainStrengthDetails: { key: "anyPainStrengthDetails", label: "Pain details", type: "text", applyMode: "append" },
  anyPainAssistanceDetails: { key: "anyPainAssistanceDetails", label: "Pain assistance details", type: "text", applyMode: "append" },
  anyWeightChanges: { key: "anyWeightChanges", label: "Any weight changes", type: "boolean", applyMode: "replace" },
  currentWeight: { key: "currentWeight", label: "Current weight", type: "text", applyMode: "replace" },
  currentHeight: { key: "currentHeight", label: "Current height", type: "text", applyMode: "replace" },
  anyWeightStrengthDetails: { key: "anyWeightStrengthDetails", label: "Weight details", type: "text", applyMode: "append" },
  anyWeightAssistanceDetails: { key: "anyWeightAssistanceDetails", label: "Weight assistance details", type: "text", applyMode: "append" },
  anyVitalSignIssues: { key: "anyVitalSignIssues", label: "Any vital sign issues", type: "boolean", applyMode: "replace" },
  anyVitalSignStrengthDetails: { key: "anyVitalSignStrengthDetails", label: "Vital sign details", type: "text", applyMode: "append" },
  anyVitalSignAssistanceDetails: { key: "anyVitalSignAssistanceDetails", label: "Vital sign assistance details", type: "text", applyMode: "append" },
  anyHospitalizations: { key: "anyHospitalizations", label: "Any hospitalizations", type: "boolean", applyMode: "replace" },
  anyHospitalizationsStrengthDetails: { key: "anyHospitalizationsStrengthDetails", label: "Hospitalization details", type: "text", applyMode: "append" },
  anyHospitalizationsAssistanceDetails: { key: "anyHospitalizationsAssistanceDetails", label: "Hospitalization assistance details", type: "text", applyMode: "append" },
  anyOtherHealthIndicatorsDetails: { key: "anyOtherHealthIndicatorsDetails", label: "Any other health indicators", type: "boolean", applyMode: "replace" },
  anyOtherHealthIndicatorsStrengthDetails: { key: "anyOtherHealthIndicatorsStrengthDetails", label: "Other health indicator details", type: "text", applyMode: "append" },
  anyOtherHealthIndicatorsAssistanceDetails: { key: "anyOtherHealthIndicatorsAssistanceDetails", label: "Other health indicator assistance details", type: "text", applyMode: "append" },
  anyAllergies: { key: "anyAllergies", label: "Any allergies", type: "boolean", applyMode: "replace" },
  allergySubstanceDetails: { key: "allergySubstanceDetails", label: "Allergy substance details", type: "text", applyMode: "append" },
  allergyReactionDetails: { key: "allergyReactionDetails", label: "Allergy reaction details", type: "text", applyMode: "append" },
};

const HEALTH_INDICATOR_VITAL_TRACKING_OPTIONS = EMAR_DAILY_TRACKING_CATEGORY_OPTIONS
  .filter((option) => option.value !== "bowel_movement")
  .map((option) => ({
    id: `vitals_tracker_${option.value}`,
    label: `${option.label} tracking applies`,
    description: option.guidance || `Document ${option.label.toLowerCase()} monitoring needs when this is part of the resident's routine care.`,
    updates: {
      anyVitalSignIssues: true,
      anyVitalSignStrengthDetails: `${option.label} monitoring may be part of the resident's ordered or routine eMAR vital tracking plan and should be reflected in health indicator documentation when relevant.`,
      anyVitalSignAssistanceDetails: `Caregiver should follow the ordered ${option.label.toLowerCase()} tracking routine, document readings consistently, and report abnormal values, worsening symptoms, or decline from baseline promptly.`,
    },
  }));

export const HEALTH_INDICATORS_GENIE_QUESTIONS: CarePlanGenieQuestion[] = [
  {
    id: "pain",
    title: "Pain",
    prompt: "What should be included about the resident's pain status and support needs?",
    editableFieldKeys: ["anyPainStrengthDetails", "anyPainAssistanceDetails"],
    options: [
      {
        id: "pain_none",
        label: "No current pain concerns",
        description: "No routine pain symptoms reported or observed.",
        updates: { anyPain: false },
      },
      {
        id: "pain_chronic",
        label: "Chronic pain history",
        description: "Pain is ongoing and should be monitored during routine care.",
        updates: {
          anyPain: true,
          anyPainStrengthDetails: "Resident has an ongoing history of pain symptoms and should be monitored for changes in comfort during routine care.",
          anyPainAssistanceDetails: "Caregiver should observe for pain cues, document concerns, and report changes so the care team can reassess comfort needs.",
        },
      },
      {
        id: "pain_positioning",
        label: "Needs comfort positioning",
        description: "Benefits from repositioning or comfort-focused support.",
        updates: {
          anyPain: true,
          anyPainAssistanceDetails: "Caregiver should assist with repositioning, pacing of activity, and other comfort measures as tolerated.",
        },
      },
      {
        id: "pain_activity_related",
        label: "Pain increases with movement or care tasks",
        description: "Pain may rise during transfers, walking, bathing, or repositioning.",
        updates: {
          anyPain: true,
          anyPainStrengthDetails: "Resident may have pain that increases with movement, transfers, personal care, repositioning, or other routine daily activities.",
          anyPainAssistanceDetails: "Caregiver should watch for activity-related pain, pace care tasks, and adjust support when discomfort increases during movement or daily care.",
        },
      },
      {
        id: "pain_reporting",
        label: "Needs pain reporting reminders",
        description: "Resident may need encouragement to report pain early.",
        updates: {
          anyPain: true,
          anyPainStrengthDetails: "Resident may not always report discomfort right away and benefits from regular check-ins about pain symptoms.",
          anyPainAssistanceDetails: "Caregiver should ask about pain during care and encourage the resident to report changes before pain becomes severe.",
        },
      },
      {
        id: "pain_nonverbal",
        label: "May show nonverbal pain cues",
        description: "Pain may appear through behavior, guarding, facial expression, or withdrawal.",
        updates: {
          anyPain: true,
          anyPainStrengthDetails: "Resident may show nonverbal pain cues such as guarding, grimacing, withdrawal, irritability, restlessness, or reduced participation when discomfort increases.",
          anyPainAssistanceDetails: "Caregiver should observe for nonverbal pain cues, document comfort concerns, and report notable changes when the resident cannot clearly describe pain.",
        },
      },
      {
        id: "pain_intervention_escalate",
        label: "Report worsening pain or reduced comfort",
        description: "Use when symptoms, tolerance, or behavior suggest pain is no longer well managed.",
        updates: {
          anyPain: true,
          anyPainAssistanceDetails: "Caregiver should report worsening pain, increased need for comfort measures, reduced activity tolerance, or new pain-related behavior changes promptly for follow-up.",
        },
      },
    ],
  },
  {
    id: "weight",
    title: "Weight",
    prompt: "What should be documented about weight, height, nutrition follow-up, or monitoring needs?",
    editableFieldKeys: ["currentWeight", "currentHeight", "anyWeightStrengthDetails", "anyWeightAssistanceDetails"],
    options: [
      {
        id: "weight_stable",
        label: "Weight is stable",
        description: "No major weight change concerns right now.",
        updates: {
          anyWeightChanges: false,
          anyWeightStrengthDetails: "Weight appears stable at this time with no major recent changes reported.",
        },
      },
      {
        id: "weight_loss",
        label: "Recent weight loss",
        description: "Needs monitoring for low intake or decline.",
        updates: {
          anyWeightChanges: true,
          anyWeightStrengthDetails: "Resident has had recent weight loss or poor intake concerns that should be monitored closely.",
          anyWeightAssistanceDetails: "Caregiver should monitor intake, encourage meals and fluids, and report continued weight decline or appetite changes.",
        },
      },
      {
        id: "weight_gain",
        label: "Recent weight gain or fluid retention",
        description: "Needs follow-up for swelling, intake, or medication-related changes.",
        updates: {
          anyWeightChanges: true,
          anyWeightStrengthDetails: "Resident has had recent weight gain, fluid retention, or body weight changes that need follow-up.",
          anyWeightAssistanceDetails: "Caregiver should monitor daily condition, observe for swelling, and report significant weight changes to the provider or nurse.",
        },
      },
      {
        id: "weight_monitoring",
        label: "Needs routine weight monitoring",
        description: "Regular checks help track trends over time.",
        updates: {
          anyWeightChanges: true,
          anyWeightAssistanceDetails: "Caregiver should assist with routine weight checks and document trends for provider review.",
        },
      },
      {
        id: "weight_appetite",
        label: "Appetite or intake affects weight risk",
        description: "Poor intake, skipping meals, or inconsistent eating may affect weight status.",
        updates: {
          anyWeightChanges: true,
          anyWeightStrengthDetails: "Weight concerns may be affected by poor appetite, inconsistent intake, skipped meals, illness, swallowing issues, or reduced interest in food and fluids.",
          anyWeightAssistanceDetails: "Caregiver should monitor meal intake, encourage nutrition and fluids as directed, and report appetite decline or poor intake patterns promptly.",
        },
      },
      {
        id: "weight_fluid_status",
        label: "Fluid status or swelling should be watched",
        description: "Helpful when edema, fluid shifts, or rapid gain/loss are part of the concern.",
        updates: {
          anyWeightChanges: true,
          anyWeightStrengthDetails: "Weight changes may reflect fluid status, edema, illness progression, or treatment-related changes rather than routine intake alone.",
          anyWeightAssistanceDetails: "Caregiver should observe for swelling, fluid retention, dehydration signs, or rapid weight shifts and report notable findings for follow-up.",
        },
      },
      {
        id: "weight_intervention_followup",
        label: "Coordinate follow-up for significant weight change",
        description: "Important when trends suggest decline, fluid issues, or nutritional risk.",
        updates: {
          anyWeightChanges: true,
          anyWeightAssistanceDetails: "Caregiver should document weight trends carefully, compare with baseline, and notify the provider or nurse when significant gain, loss, or nutrition-related decline is identified.",
        },
      },
    ],
  },
  {
    id: "vitals",
    title: "Vital Signs",
    prompt: "What monitoring or support is needed around blood pressure, pulse, temperature, oxygen, or other vital signs?",
    editableFieldKeys: ["anyVitalSignStrengthDetails", "anyVitalSignAssistanceDetails"],
    options: [
      {
        id: "vitals_none",
        label: "No current vital sign concerns",
        description: "No active vital sign issue to highlight.",
        updates: { anyVitalSignIssues: false },
      },
      {
        id: "vitals_monitoring",
        label: "Needs routine monitoring",
        description: "Vitals should be checked on a regular schedule.",
        updates: {
          anyVitalSignIssues: true,
          anyVitalSignStrengthDetails: "Resident needs routine vital sign monitoring to track overall condition and identify changes promptly.",
          anyVitalSignAssistanceDetails: "Caregiver should follow the monitoring schedule, document readings, and report results outside the expected range.",
        },
      },
      {
        id: "vitals_symptoms",
        label: "Symptoms can affect vital signs",
        description: "Condition changes may need follow-up when symptoms increase.",
        updates: {
          anyVitalSignIssues: true,
          anyVitalSignStrengthDetails: "Resident may have symptoms or conditions that can affect vital signs and require closer observation during changes in status.",
          anyVitalSignAssistanceDetails: "Caregiver should monitor symptoms along with vital signs and escalate concerns when readings or symptoms worsen.",
        },
      },
      {
        id: "vitals_parameters",
        label: "Out-of-range readings need prompt reporting",
        description: "Useful when the resident has clear provider parameters or clinical risk.",
        updates: {
          anyVitalSignIssues: true,
          anyVitalSignStrengthDetails: "Resident may need closer follow-up when blood pressure, pulse, temperature, oxygen level, or other vital signs fall outside the expected range.",
          anyVitalSignAssistanceDetails: "Caregiver should recheck abnormal readings when indicated, compare with symptoms, and report out-of-range results promptly according to the care plan or provider guidance.",
        },
      },
      {
        id: "vitals_orthostatic",
        label: "Position changes may affect readings or symptoms",
        description: "Dizziness, weakness, or orthostatic symptoms may matter during transfers or ambulation.",
        updates: {
          anyVitalSignIssues: true,
          anyVitalSignStrengthDetails: "Vital sign concerns may increase with standing, transfers, activity, illness, or dehydration and should be considered along with the resident's symptoms.",
          anyVitalSignAssistanceDetails: "Caregiver should observe for dizziness, weakness, shortness of breath, or other symptoms during position changes and report concerns when vital signs and symptoms suggest decline.",
        },
      },
      {
        id: "vitals_intervention_consistency",
        label: "Use a consistent monitoring routine",
        description: "Helpful when readings need to be comparable across shifts.",
        updates: {
          anyVitalSignIssues: true,
          anyVitalSignAssistanceDetails: "Caregiver should use a consistent routine for vital sign checks, document the resident's condition at the time of the reading, and communicate significant changes across shifts.",
        },
      },
      ...HEALTH_INDICATOR_VITAL_TRACKING_OPTIONS,
    ],
  },
  {
    id: "hospitalizations",
    title: "Hospitalizations",
    prompt: "What should be documented about recent hospitalizations, emergency visits, or follow-up care?",
    editableFieldKeys: ["anyHospitalizationsStrengthDetails", "anyHospitalizationsAssistanceDetails"],
    options: [
      {
        id: "hospital_none",
        label: "No recent hospital or ER visit concerns",
        description: "No recent transition details to add.",
        updates: { anyHospitalizations: false },
      },
      {
        id: "hospital_recent",
        label: "Recent hospitalization or ER visit",
        description: "Needs follow-up and status monitoring after discharge.",
        updates: {
          anyHospitalizations: true,
          anyHospitalizationsStrengthDetails: "Resident has had a recent hospitalization or emergency visit and may need follow-up for recovery, new orders, or symptom monitoring.",
          anyHospitalizationsAssistanceDetails: "Caregiver should watch for recurrence of symptoms, follow discharge instructions, and report concerns promptly.",
        },
      },
      {
        id: "hospital_appointments",
        label: "Needs post-discharge follow-up",
        description: "Appointments or medication changes should be tracked.",
        updates: {
          anyHospitalizations: true,
          anyHospitalizationsAssistanceDetails: "Caregiver should help coordinate follow-up appointments, review updated orders, and monitor for changes after discharge.",
        },
      },
      {
        id: "hospital_orders",
        label: "New orders or care changes should be reinforced",
        description: "Use when recent transitions changed medications, diet, monitoring, or support needs.",
        updates: {
          anyHospitalizations: true,
          anyHospitalizationsStrengthDetails: "Recent hospitalization or emergency care may have resulted in new orders, medication changes, follow-up needs, or revised monitoring expectations.",
          anyHospitalizationsAssistanceDetails: "Caregiver should review discharge-related changes carefully, reinforce updated care instructions, and watch for problems with follow-through after transition back to the home.",
        },
      },
      {
        id: "hospital_redflags",
        label: "Watch for return of symptoms or complications",
        description: "Important when the resident is at risk for readmission or worsening condition.",
        updates: {
          anyHospitalizations: true,
          anyHospitalizationsAssistanceDetails: "Caregiver should watch closely for return of symptoms, complications, worsening condition, or difficulty recovering after discharge and report concerns promptly.",
        },
      },
    ],
  },
  {
    id: "other",
    title: "Other Indicators",
    prompt: "Are there other important health indicators, conditions, or observations that caregiver should monitor?",
    editableFieldKeys: ["anyOtherHealthIndicatorsStrengthDetails", "anyOtherHealthIndicatorsAssistanceDetails"],
    options: [
      {
        id: "other_none",
        label: "No additional health indicators",
        description: "Nothing additional needs to be highlighted right now.",
        updates: { anyOtherHealthIndicatorsDetails: false },
      },
      {
        id: "other_monitoring",
        label: "Ongoing condition monitoring needed",
        description: "Resident has other health issues that should stay on the care team's radar.",
        updates: {
          anyOtherHealthIndicatorsDetails: true,
          anyOtherHealthIndicatorsStrengthDetails: "Resident has other health indicators or chronic condition concerns that require ongoing observation during daily care.",
          anyOtherHealthIndicatorsAssistanceDetails: "Caregiver should monitor for status changes, document observations, and communicate concerns to the provider or nurse.",
        },
      },
      {
        id: "other_changes",
        label: "Condition changes should be reported quickly",
        description: "New symptoms or decline need prompt reporting.",
        updates: {
          anyOtherHealthIndicatorsDetails: true,
          anyOtherHealthIndicatorsAssistanceDetails: "Caregiver should promptly report new symptoms, decline in condition, or other concerning changes.",
        },
      },
      {
        id: "other_chronic",
        label: "Chronic conditions require daily observation",
        description: "Helpful when baseline chronic issues affect routine care and monitoring.",
        updates: {
          anyOtherHealthIndicatorsDetails: true,
          anyOtherHealthIndicatorsStrengthDetails: "Resident has chronic health conditions or baseline clinical concerns that require daily observation for subtle changes in status.",
          anyOtherHealthIndicatorsAssistanceDetails: "Caregiver should monitor routine condition changes, compare with baseline functioning, and communicate concerns early when chronic issues appear to worsen.",
        },
      },
      {
        id: "other_escalation",
        label: "Escalate decline from baseline promptly",
        description: "Use when small changes may signal more serious clinical issues.",
        updates: {
          anyOtherHealthIndicatorsDetails: true,
          anyOtherHealthIndicatorsAssistanceDetails: "Caregiver should note and escalate decline from baseline, worsening symptoms, or new clinical concerns promptly so follow-up can happen before the condition worsens.",
        },
      },
    ],
  },
  {
    id: "allergies",
    title: "Allergies",
    prompt: "What allergy information should be included for caregiver awareness and safety?",
    editableFieldKeys: ["allergySubstanceDetails", "allergyReactionDetails"],
    options: [
      {
        id: "allergy_none",
        label: "No known allergies",
        description: "No allergy concerns reported at this time.",
        updates: { anyAllergies: false },
      },
      {
        id: "allergy_medication",
        label: "Medication allergy",
        description: "List medication triggers and the reaction history.",
        updates: {
          anyAllergies: true,
          allergySubstanceDetails: "Resident has a known medication allergy that should be clearly listed in the care plan and medication records.",
          allergyReactionDetails: "Caregiver should be aware of the resident's allergy history and watch for reported or observed reactions if exposure is suspected.",
        },
      },
      {
        id: "allergy_food",
        label: "Food or environmental allergy",
        description: "Dietary or exposure precautions are needed.",
        updates: {
          anyAllergies: true,
          allergySubstanceDetails: "Resident has a food or environmental allergy that should be communicated to caregiver for routine safety precautions.",
          allergyReactionDetails: "Caregiver should avoid known triggers when possible and report allergy symptoms immediately if exposure occurs.",
        },
      },
      {
        id: "allergy_reaction_history",
        label: "Reaction type should be clearly documented",
        description: "Use when the substance is known but reaction details need to be emphasized.",
        updates: {
          anyAllergies: true,
          allergyReactionDetails: "Document the resident's known allergy reaction history clearly, including rash, swelling, breathing concerns, gastrointestinal symptoms, or other reported reaction pattern.",
        },
      },
      {
        id: "allergy_intervention_precautions",
        label: "Use consistent exposure precautions",
        description: "Helpful when caregiver need the same safety reminders across meals, meds, and routine care.",
        updates: {
          anyAllergies: true,
          allergyReactionDetails: "Caregiver should verify known allergy precautions during meals, medication support, product use, and other routine care interactions to reduce exposure risk.",
        },
      },
    ],
  },
];

export const buildHealthIndicatorsGeniePreview = (
  currentValues: Record<string, any>,
  selectedOptions: Record<string, string[]>,
  questionDrafts: Record<string, Record<string, string>>,
) => buildCarePlanGeniePreview(
  HEALTH_INDICATORS_GENIE_FIELD_DEFINITIONS,
  HEALTH_INDICATORS_GENIE_QUESTIONS,
  currentValues,
  selectedOptions,
  questionDrafts,
);
